import React from 'react';

import get from 'lodash/fp/get';
import uniqueId from 'lodash/uniqueId';

import { margin } from '@lumapps/classnames';
import { CustomRadioList } from '@lumapps/designer-widget-settings/components/CustomRadioList';
import { ViewModeBlock } from '@lumapps/designer-widget-settings/components/ViewModeBlock';
import { WIDGET_SETTINGS } from '@lumapps/designer-widget-settings/keys';
import { ViewModeType } from '@lumapps/designer-widget-settings/types';
import { Heading } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';

import { DEFAULT_ORIENTATION, ORIENTATION_VARIANTS } from '../../constants';
import { WidgetPlayPropertiesType } from '../../types';

import './index.scss';

export interface WidgetPlayStyleProps {
    properties: WidgetPlayPropertiesType;
    onChange: (properties: WidgetPlayPropertiesType) => void;
}

const CLASSNAME = 'widget-play-style';

export const WidgetPlayStyle = ({ properties, onChange }: WidgetPlayStyleProps) => {
    const { translateKey } = useTranslate();

    const orientationLabelId = React.useMemo(() => uniqueId(`${CLASSNAME}-orientation-label-`), []);

    if (properties.orientation === undefined) {
        return null;
    }

    const selectedOrientation =
        ORIENTATION_VARIANTS.find((v) => v.value === (properties.orientation ?? DEFAULT_ORIENTATION)) ||
        ORIENTATION_VARIANTS[0];

    return (
        <div className={CLASSNAME}>
            <Heading as="h4" typography="subtitle1" id={orientationLabelId} className={margin('bottom', 'huge')}>
                {translateKey(WIDGET_SETTINGS.VIEW_MODE)}
            </Heading>
            <CustomRadioList
                aria-labelledby={orientationLabelId}
                items={ORIENTATION_VARIANTS.map((viewMode) => ({ viewMode }))}
                dataScope={CLASSNAME}
                itemRenderer={ViewModeBlock}
                itemKeySelector={get('viewMode.value')}
                selectedItem={{ viewMode: selectedOrientation }}
                setSelectedItem={(item: { viewMode: ViewModeType<WidgetPlayPropertiesType['orientation']> }) => {
                    onChange({ ...properties, orientation: item.viewMode.value });
                }}
            />
        </div>
    );
};
